import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';

/** Global age-assurance gate. Listens for `hibff:verification-gate` events
 *  dispatched by the API client when the backend blocks AvA chat with one of:
 *  age_declaration_required | parent_consent_pending | age_out_of_range
 *  Consumer HiBFF is strictly for teens aged 13-19 — every declared teen
 *  must supply real parent/guardian details. */
export const VerificationGateModal = () => {
  const [gate, setGate] = useState(null);
  const [dob, setDob] = useState('');
  const [parent, setParent] = useState({ name: '', email: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onGate = (e) => setGate(e.detail?.code || null);
    window.addEventListener('hibff:verification-gate', onGate);
    return () => window.removeEventListener('hibff:verification-gate', onGate);
  }, []);

  if (!gate) return null;

  const ageFromDob = (d) => {
    const b = new Date(d); const t = new Date();
    let a = t.getFullYear() - b.getFullYear();
    if (t < new Date(t.getFullYear(), b.getMonth(), b.getDate())) a -= 1;
    return a;
  };

  const declaredAge = dob ? ageFromDob(dob) : null;
  const inTeenRange = declaredAge !== null && declaredAge >= 13 && declaredAge <= 19;
  const overRange = declaredAge !== null && declaredAge > 19;

  const submitDob = async () => {
    setError('');
    if (!dob) { setError('Please enter your date of birth.'); return; }
    if (declaredAge < 13) { setError('HiBFF is for ages 13 and up.'); return; }
    if (overRange) { setError('HiBFF teen accounts are for ages 13\u201319.'); return; }
    if (!parent.name || !parent.email || !parent.phone) {
      setError("Your parent or guardian's full name, email and mobile number are required.");
      return;
    }
    setBusy(true);
    try {
      await apiClient.request('/api/age-gate/declare-age', {
        method: 'POST',
        body: JSON.stringify({
          dob,
          parent_name: parent.name,
          parent_email: parent.email,
          parent_phone: parent.phone,
        }),
      });
      setGate('parent_consent_pending');
    } catch (e2) {
      setError(e2?.message || 'Could not save. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="verification-gate-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
        {gate === 'age_declaration_required' && (
          <>
            <h2 className="text-lg font-bold text-gray-900">Confirm your date of birth</h2>
            <p className="text-sm text-gray-600">HiBFF is built for teens aged 13&ndash;19, supported by a parent or guardian &mdash; we need your real date of birth before you can chat with an AvA.</p>
            <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
              className="w-full border rounded-lg px-3 py-2" data-testid="age-gate-dob-input" />
            {inTeenRange && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-800">We'll invite your parent or guardian:</p>
                <input placeholder="Parent/Guardian full name" value={parent.name} onChange={(e) => setParent({ ...parent, name: e.target.value })} className="w-full border rounded-lg px-3 py-2" data-testid="age-gate-parent-name" />
                <input placeholder="Parent/Guardian email" type="email" value={parent.email} onChange={(e) => setParent({ ...parent, email: e.target.value })} className="w-full border rounded-lg px-3 py-2" data-testid="age-gate-parent-email" />
                <input placeholder="Parent/Guardian mobile (incl. country code)" value={parent.phone} onChange={(e) => setParent({ ...parent, phone: e.target.value })} className="w-full border rounded-lg px-3 py-2" data-testid="age-gate-parent-phone" />
              </div>
            )}
            {overRange && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2" data-testid="age-out-of-range-panel">
                <p className="text-sm text-amber-900">HiBFF teen accounts are for ages 13&ndash;19. If you're a parent, create a parent account to support your teen instead.</p>
                <a href="/parent-register" className="block text-center w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-2 text-sm font-semibold" data-testid="age-gate-parent-cta">Create a parent account</a>
              </div>
            )}
            {error && <p className="text-sm text-red-600" data-testid="age-gate-error">{error}</p>}
            {!overRange && (
              <button onClick={submitDob} disabled={busy} className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-2.5 font-semibold disabled:opacity-50" data-testid="age-gate-submit">
                {busy ? 'Saving\u2026' : 'Continue'}
              </button>
            )}
          </>
        )}
        {gate === 'age_out_of_range' && (
          <>
            <h2 className="text-lg font-bold text-gray-900">HiBFF is for teens aged 13&ndash;19</h2>
            <p className="text-sm text-gray-600">Teen accounts are only available to 13&ndash;19 year olds. If you're a parent or guardian, create a parent account to support your teen's journey.</p>
            <a href="/parent-register" className="block text-center w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-2.5 font-semibold" data-testid="age-gate-parent-cta">Create a parent account</a>
          </>
        )}
        {gate === 'parent_consent_pending' && (
          <>
            <h2 className="text-lg font-bold text-gray-900">Waiting for your parent</h2>
            <p className="text-sm text-gray-600">Thanks for being honest about your age. We've sent your parent or guardian an <strong>email and a text</strong> with an approval link &mdash; AvA chat unlocks the moment they approve your account.</p>
            <button onClick={() => setGate(null)} className="w-full border rounded-lg py-2.5 font-medium" data-testid="consent-pending-close">OK</button>
          </>
        )}
        {gate !== 'parent_consent_pending' && (
          <button onClick={() => setGate(null)} className="w-full text-sm text-gray-400 hover:text-gray-600" data-testid="verification-gate-dismiss">Not now</button>
        )}
      </div>
    </div>
  );
};

export default VerificationGateModal;
